import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  TRAVEL_FILE_TYPES,
  TRAVEL_TYPES,
  TRAVEL_TYPE_LABELS,
  checkTravelItem,
  eventName,
  isFileType,
  itemTitle,
  readingItems,
  type ConfirmationItem,
  type ConfirmationReading,
  type TravelDraft,
  type TravelFields,
  type TravelItem,
  type TravelType,
  travelReadFailureText,
} from '@wr/shared';
import { ApiError, del, errorMessage, get, patch, post, putFile } from '../../api.js';
import { Icon, type IconName } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Checkbox, Dialog, Field, ManyOfChips, Notice } from '../../components/ui.js';
import { newId } from '../../offline/ids.js';
import { useSignedIn } from '../../session.js';
import { waitForDraft } from './data.js';
import { useTrip } from './TravelScreen.js';

const ICONS: Record<TravelType, IconName> = {
  flight: 'plane',
  hotel: 'bed',
  car: 'pin',
  train: 'train',
  badge: 'ticket',
  document: 'pdf',
  other: 'edit',
};
const HINTS: Record<TravelType, string> = {
  flight: 'From the confirmation or the flight number',
  hotel: 'Reservation and confirmation',
  car: 'Pick-up and return',
  train: 'Ticket and times',
  badge: 'Personal or team badges',
  document: 'Hall plan, map, any file',
  other: 'Shuttle, restaurant, anything',
};

/** Add: the type first (prototype: openAddBooking). */
export function AddWindow() {
  const { page } = useTrip();
  const navigate = useNavigate();
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });
  return (
    <Dialog
      title={`Add to ${eventName(page.event)}`}
      onClose={close}
      footer={
        <button type="button" className="btn" onClick={close}>
          Cancel
        </button>
      }
    >
      <p className="travel-intro">
        What do you want to add? You can add it for yourself or for others on the team.
      </p>
      <div className="bk-ways">
        {TRAVEL_TYPES.map((type) => (
          <button
            key={type}
            type="button"
            className="bk-way"
            onClick={() =>
              navigate(`/travel/${page.event.id}/add/${type}${isFileType(type) ? '' : '/how'}`, {
                replace: true,
              })
            }
          >
            <Icon name={ICONS[type]} />
            <b>{TRAVEL_TYPE_LABELS[type]}</b>
            <span>{HINTS[type]}</span>
          </button>
        ))}
      </div>
    </Dialog>
  );
}

/**
 * The way to add a booking (prototype: chooseWay): upload the confirmation,
 * forward the email to the show's address, or type it. Uploaded and
 * forwarded ones are read by Claude into a draft to check (brief 7.3).
 */
export function WayWindow() {
  const { type: typeParam = '' } = useParams();
  const { page, reload } = useTrip();
  const navigate = useNavigate();
  const type = typeParam as TravelType;
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });
  const [reading, setReading] = useState<string | null>(null);
  const [forward, setForward] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const label = TRAVEL_TYPE_LABELS[type] ?? 'Booking';

  async function upload(file: File | undefined) {
    if (!file) return;
    if (!TRAVEL_FILE_TYPES.includes(file.type)) {
      setError('The confirmation must be a PDF or a picture.');
      return;
    }
    setError(null);
    setReading(file.name);
    try {
      const draft = await putFile<TravelDraft>(
        `/travel/events/${page.event.id}/drafts?type=${type}&name=${encodeURIComponent(file.name)}`,
        file,
        'POST',
      );
      const read = await waitForDraft(draft.id);
      reload();
      if (read.status === 'ready') {
        navigate(`/travel/${page.event.id}/drafts/${read.id}`, { replace: true });
      } else {
        setReading(null);
        setError(travelReadFailureText(read.failure));
      }
    } catch (err) {
      setReading(null);
      setError(
        err instanceof ApiError && err.status === 504
          ? 'The reading takes longer than usual: it will appear on the trip page, to confirm.'
          : errorMessage(err),
      );
    }
  }

  return (
    <Dialog
      title={`${label}: ${eventName(page.event)}`}
      onClose={close}
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => navigate(`/travel/${page.event.id}/add`, { replace: true })}
          >
            Back
          </button>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
        </>
      }
    >
      <p className="travel-intro">
        Pick the easiest way. You check the result and who it is for before it is saved.
      </p>
      <div className="bk-ways">
        <label className="bk-way">
          <Icon name="pdf" />
          <b>Upload the confirmation</b>
          <span>PDF or screenshot</span>
          <input
            type="file"
            hidden
            accept="application/pdf,image/*"
            disabled={Boolean(reading)}
            onChange={(e) => {
              void upload(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </label>
        <button
          type="button"
          className="bk-way"
          disabled={!page.inboundAddress}
          onClick={() => setForward(true)}
        >
          <Icon name="mail" />
          <b>Forward the email</b>
          <span>{page.inboundAddress ? "To the show's portal address" : 'Not set up yet'}</span>
        </button>
        <button
          type="button"
          className="bk-way"
          onClick={() => navigate(`/travel/${page.event.id}/add/${type}`, { replace: true })}
        >
          <Icon name="edit" />
          <b>Type it</b>
          <span>{type === 'flight' ? 'Flight number and times' : 'A few fields'}</span>
        </button>
      </div>
      {reading && (
        <div className="sstep run">
          <span className="si" />
          <span>Reading {reading}</span>
        </div>
      )}
      {forward && page.inboundAddress && (
        <div className="formsec">
          <p className="travel-intro">Forward the confirmation to:</p>
          <div className="sharebox">
            <input type="text" readOnly aria-label="Forward to" value={page.inboundAddress} />
            <button
              type="button"
              className="btn sm"
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(page.inboundAddress ?? '')
                  .then(() => setCopied(true))
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          <p className="note travel-intro">
            Forward it from your work email. It arrives on this trip page as a draft to confirm,
            usually within a minute.
          </p>
        </div>
      )}
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}

type Input = { name: string; label: string; type?: string; placeholder?: string };
const F = (name: string, label: string, type = 'text', placeholder?: string): Input => ({
  name,
  label,
  type,
  placeholder,
});

/** The fields of each type, in pairs as the prototype lays them out. */
const LAYOUT: Record<TravelType, (Input | [Input, Input])[]> = {
  flight: [
    F('number', 'Flight number', 'text', 'e.g. AC 846'),
    [F('carrier', 'Airline'), F('reference', 'Booking reference')],
    [F('from', 'From'), F('to', 'To')],
    [F('depart', 'Departure', 'datetime-local'), F('arrive', 'Arrival', 'datetime-local')],
    [F('seat', 'Seat'), F('link', 'Booking link', 'url')],
  ],
  hotel: [
    F('name', 'Hotel'),
    F('address', 'Address'),
    [F('check_in', 'Check-in', 'date'), F('check_out', 'Check-out', 'date')],
    [F('reference', 'Confirmation number'), F('link', 'Reservation link', 'url')],
    F('to_venue', 'To the venue', 'text', 'e.g. 8 minutes on foot'),
  ],
  car: [
    F('company', 'Rental company'),
    F('pickup', 'Pick-up location'),
    [F('pickup_at', 'Pick-up', 'datetime-local'), F('return_at', 'Return', 'datetime-local')],
    [F('reference', 'Confirmation number'), F('link', 'Booking link', 'url')],
  ],
  train: [
    [F('number', 'Train number', 'text', 'e.g. FR 9518'), F('operator', 'Operator')],
    [F('from', 'From'), F('to', 'To')],
    [F('depart', 'Departure', 'datetime-local'), F('arrive', 'Arrival', 'datetime-local')],
    [F('seat', 'Coach and seat'), F('reference', 'Ticket reference')],
    F('link', 'Ticket link', 'url'),
  ],
  badge: [F('title', 'Title', 'text', 'Exhibitor badge'), F('reference', 'Badge number or code')],
  document: [F('title', 'Title', 'text', 'Hall B6 floor plan'), F('detail', 'Note, optional')],
  other: [
    F('title', 'What', 'text', 'Shuttle, restaurant, meeting'),
    F('detail', 'Details'),
    [F('when', 'When', 'datetime-local'), F('reference', 'Reference')],
    F('link', 'Link', 'url'),
  ],
};

/** A booking's fields as the prototype lays them out: the direction of a flight, then its fields. */
function BookingFields({
  type,
  values,
  set,
}: {
  type: TravelType;
  values: TravelFields;
  set(name: string, value: string): void;
}) {
  const field = (input: Input) => (
    <Field
      key={input.name}
      label={input.label}
      type={input.type}
      placeholder={input.placeholder}
      value={values[input.name] ?? ''}
      onChange={(e) => set(input.name, e.target.value)}
    />
  );
  return (
    <>
      {(type === 'flight' || type === 'train') && (
        <div className="seg" role="group" aria-label="Direction">
          {(
            [
              ['out', 'Outbound'],
              ['back', 'Return'],
            ] as const
          ).map(([dir, label]) => (
            <button
              key={dir}
              type="button"
              aria-pressed={(values.dir ?? 'out') === dir}
              onClick={() => set('dir', dir)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      {LAYOUT[type].map((row) =>
        Array.isArray(row) ? (
          <div key={row[0].name} className="g2">
            {field(row[0])}
            {field(row[1])}
          </div>
        ) : (
          field(row)
        ),
      )}
      {type === 'document' && (
        <div className="seg" role="group" aria-label="Kind">
          {(
            [
              ['plan', 'Plan or map'],
              ['doc', 'Document'],
            ] as const
          ).map(([kind, label]) => (
            <button
              key={kind}
              type="button"
              aria-pressed={(values.kind ?? 'plan') === kind}
              onClick={() => set('kind', kind)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

/** Add or edit an item: its fields, who it is for, its file (prototype: openBookingForm). */
export function ItemWindow({ draft }: { draft?: TravelDraft }) {
  const { type: typeParam, itemId } = useParams();
  const { page, reload } = useTrip();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useSignedIn();
  const editing = itemId ? page.items.find((i) => i.id === itemId) : undefined;
  const reading =
    draft?.status === 'ready' && draft.result && 'passengers' in draft.result
      ? draft.result
      : undefined;
  const type = (editing?.type ?? reading?.type ?? draft?.type ?? typeParam) as TravelType;
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });

  const [id] = useState(() => editing?.id ?? newId());
  const [values, setValues] = useState<TravelFields>(() => ({
    ...(type === 'flight' || type === 'train'
      ? { dir: 'out' }
      : type === 'document'
        ? { kind: 'plan' }
        : {}),
    ...(editing?.fields ?? reading?.fields),
    reference: editing?.reference ?? reading?.reference ?? '',
    link: editing?.link ?? reading?.link ?? '',
  }));
  const forAll = isFileType(type);
  const [who, setWho] = useState<string[]>(() =>
    editing
      ? editing.everyone
        ? ['everyone']
        : editing.travelers
      : reading?.travelers.length
        ? reading.travelers
        : page.team.some((m) => m.id === user.id)
          ? [user.id]
          : [],
  );
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [dup, setDup] = useState<{ id: string; title: string; added_by_name: string } | null>(null);

  if (!TRAVEL_TYPES.includes(type) || (itemId && !editing)) {
    return (
      <Dialog small title="Travel" onClose={close}>
        <Notice kind="error">This item does not exist, or it was deleted.</Notice>
      </Dialog>
    );
  }

  const set = (name: string, value: string) => {
    setValues((v) => ({ ...v, [name]: value }));
    setError(null);
    setDup(null);
  };
  const travelers = who.filter((w) => w !== 'everyone');
  const body = () => {
    const { reference = '', link = '', ...fields } = values;
    return {
      id,
      type,
      fields,
      reference,
      link,
      travelers,
      everyone: forAll && who.includes('everyone'),
      file: Boolean(file),
      ...(draft ? { draft_id: draft.id } : {}),
    };
  };

  async function save(e?: FormEvent) {
    e?.preventDefault();
    if (busy) return;
    const check = checkTravelItem(
      body(),
      Boolean(file) || Boolean(editing?.has_file) || Boolean(draft?.file_name),
    );
    if (!check.ok) return setError(check.message);
    if (file && !TRAVEL_FILE_TYPES.includes(file.type)) {
      return setError('The file must be a PDF or a picture.');
    }
    setBusy(true);
    try {
      const saved = editing
        ? await patch<TravelItem>(`/travel/items/${id}`, body())
        : await post<TravelItem>(`/travel/events/${page.event.id}/items`, body());
      if (file) {
        await putFile(`/travel/items/${saved.id}/file?name=${encodeURIComponent(file.name)}`, file);
      }
      toast(editing || draft ? 'Saved' : `${TRAVEL_TYPE_LABELS[type]} added`);
      reload();
      close();
    } catch (err) {
      const existing = err instanceof ApiError ? err.body.existing : undefined;
      if (err instanceof ApiError && err.status === 409 && existing) {
        setDup(existing as { id: string; title: string; added_by_name: string });
      } else {
        const message = err instanceof ApiError ? err.body.message : undefined;
        setError(typeof message === 'string' ? message : errorMessage(err));
      }
    } finally {
      setBusy(false);
    }
  }

  async function addToExisting() {
    if (!dup) return;
    setBusy(true);
    try {
      await post(`/travel/items/${dup.id}/travelers`, { travelers });
      // The same booking was there already: the draft is no longer needed.
      if (draft) await del(`/travel/drafts/${draft.id}`);
      toast('Added to the existing one');
      reload();
      close();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  async function remove() {
    setBusy(true);
    try {
      await del(draft ? `/travel/drafts/${draft.id}` : `/travel/items/${id}`);
      toast(draft ? 'Dismissed' : 'Removed');
      reload();
      close();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={`${editing ? 'Edit' : draft ? 'Check' : 'Add'}: ${TRAVEL_TYPE_LABELS[type]}`}
      onClose={close}
      footer={
        <>
          {(editing || draft) && (
            <button
              type="button"
              className="btn danger left"
              disabled={busy}
              onClick={() => void remove()}
            >
              {draft ? 'Dismiss' : 'Delete'}
            </button>
          )}
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
            {draft ? 'Confirm and save' : 'Save'}
          </button>
        </>
      }
    >
      <form id="travel-item" className="travel-form" noValidate onSubmit={(e) => void save(e)}>
        {draft && (
          <div className="hs-state wait">
            <b>Read by Claude</b>
            <span>Check every field and who it is for before saving.</span>
          </div>
        )}
        <BookingFields type={type} values={values} set={set} />
        {forAll && (
          <label className="f">
            <span>{type === 'badge' ? 'File, optional' : 'File'}</span>
            <input
              type="file"
              accept="application/pdf,image/*"
              onChange={(e) => {
                setFile(e.target.files?.[0] ?? null);
                setError(null);
              }}
            />
          </label>
        )}
        {forAll && editing?.file_name && !file && (
          <p className="note travel-note">Current file: {editing.file_name}</p>
        )}
        <ManyOfChips
          label={forAll ? 'For' : 'Who travels on this booking'}
          options={[
            ...(forAll ? [{ value: 'everyone', label: 'Everyone on the team' }] : []),
            ...page.team.map((m) => ({ value: m.id, label: m.name })),
          ]}
          value={who}
          onChange={(v) => {
            setWho(v);
            setError(null);
          }}
        />
        {reading && reading.passengers.length > 0 && (
          <small className="note">
            Names found in the confirmation: {reading.passengers.join(', ')}
          </small>
        )}
        {draft?.file_name && !forAll && (
          <p className="note travel-intro">The confirmation is kept with it: {draft.file_name}</p>
        )}
        {page.team.length === 0 && (
          <Notice kind="warn">
            Nobody is on the team of this show yet: an admin adds the team.
          </Notice>
        )}
        {dup && (
          <div className="dup" role="alert">
            <b>Already there</b>
            <span>
              Something with reference {values.reference} already exists
              {dup.added_by_name ? ` (added by ${dup.added_by_name})` : ''}: {dup.title}. Add these
              people to it instead of creating a duplicate?
            </span>
            <div className="actions">
              <button
                type="button"
                className="btn sm primary"
                disabled={busy}
                onClick={() => void addToExisting()}
              >
                Add them to it
              </button>
              <button type="button" className="btn sm" onClick={() => setDup(null)}>
                Change the reference
              </button>
            </div>
          </div>
        )}
        <p className="err" role="alert">
          {error}
        </p>
      </form>
    </Dialog>
  );
}

/**
 * A confirmation read by Claude, to check (route drafts/:draftId): from the
 * trip page when it has it, else from the portal (it was just read).
 */
export function DraftWindow() {
  const { draftId = '' } = useParams();
  const { page } = useTrip();
  const navigate = useNavigate();
  const known = page.drafts.find((d) => d.id === draftId);
  const [draft, setDraft] = useState<TravelDraft | null>(known ?? null);
  const [error, setError] = useState<string | null>(null);
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });

  useEffect(() => {
    if (draft) return;
    let current = true;
    get<TravelDraft>(`/travel/drafts/${draftId}`).then(
      (d) => current && setDraft(d),
      (err: unknown) => current && setError(errorMessage(err)),
    );
    return () => {
      current = false;
    };
  }, [draft, draftId]);

  if (draft?.status === 'ready' && draft.result && 'passengers' in draft.result) {
    // Round 2: a confirmation with several bookings (a round trip, a hotel too) checks them all at once.
    return readingItems(draft.result).length > 1 ? (
      <DraftItemsWindow key={draft.id} draft={draft} reading={draft.result} />
    ) : (
      <ItemWindow key={draft.id} draft={draft} />
    );
  }
  return (
    <Dialog small title="Check the confirmation" onClose={close}>
      {error ? (
        <Notice kind="error">{error}</Notice>
      ) : draft?.status === 'failed' ? (
        <Notice kind="error">{travelReadFailureText(draft.failure)}</Notice>
      ) : draft ? (
        <div className="sstep run">
          <span className="si" />
          <span>Reading {draft.file_name ?? draft.subject ?? 'the confirmation'}</span>
        </div>
      ) : (
        <div className="spinner" aria-label="Loading" />
      )}
    </Dialog>
  );
}

/** "Outbound flight: Northwind Air NW 318, YUL Montréal to AMS Amsterdam". */
function bookingLabel(item: Pick<ConfirmationItem, 'type' | 'fields'>): string {
  const f = item.fields;
  const kind =
    item.type === 'flight' || item.type === 'train'
      ? `${f.dir === 'back' ? 'Return' : 'Outbound'} ${item.type}`
      : TRAVEL_TYPE_LABELS[item.type];
  const route = f.from && f.to ? `${f.from} to ${f.to}` : '';
  return `${kind}: ${[itemTitle(item), route].filter(Boolean).join(', ')}`;
}

/** The window's title: "2 flights", "2 flights and a hotel", "3 bookings". */
function bookingsTitle(items: readonly ConfirmationItem[]): string {
  return items.every((i) => i.type === 'flight')
    ? `${items.length} flights`
    : `${items.length} bookings`;
}

type Kept = ConfirmationItem & { id: string; keep: boolean };

/**
 * Every booking of a confirmation, checked together (round 2): each one
 * ticked, with its fields; untick those not wanted, then confirm them all
 * at once for the same travelers. One already on the trip page gets them
 * instead of a duplicate.
 */
function DraftItemsWindow({
  draft,
  reading,
}: {
  draft: TravelDraft;
  reading: ConfirmationReading;
}) {
  const { page, reload } = useTrip();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useSignedIn();
  const close = () => navigate(`/travel/${page.event.id}`, { replace: true });
  const [items, setItems] = useState<Kept[]>(() =>
    readingItems(reading).map((i) => ({
      ...i,
      fields: { ...(i.type === 'flight' || i.type === 'train' ? { dir: 'out' } : {}), ...i.fields },
      id: newId(),
      keep: true,
    })),
  );
  const [who, setWho] = useState<string[]>(() =>
    reading.travelers.length
      ? reading.travelers
      : page.team.some((m) => m.id === user.id)
        ? [user.id]
        : [],
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const kept = items.filter((i) => i.keep);

  const change = (id: string, next: (item: Kept) => Kept) => {
    setItems((list) => list.map((i) => (i.id === id ? next(i) : i)));
    setError(null);
  };
  const setField = (id: string, name: string, value: string) =>
    change(id, (i) =>
      name === 'reference' || name === 'link'
        ? { ...i, [name]: value }
        : { ...i, fields: { ...i.fields, [name]: value } },
    );

  async function confirm() {
    if (busy || kept.length === 0) return;
    for (const item of kept) {
      const check = checkTravelItem({ ...item, travelers: who, everyone: false }, true);
      if (!check.ok) return setError(`${bookingLabel(item)}: ${check.message}`);
    }
    setBusy(true);
    try {
      const res = await post<{ created: number; joined: { id: string; title: string }[] }>(
        `/travel/drafts/${draft.id}/confirm`,
        {
          items: kept.map(({ id, type, fields, reference, link }) => ({
            id,
            type,
            fields,
            reference,
            link,
          })),
          travelers: who,
        },
      );
      toast(
        [
          res.created ? `${res.created} saved` : '',
          res.joined.length ? `${res.joined.length} added to the existing one` : '',
        ]
          .filter(Boolean)
          .join(', '),
      );
      reload();
      close();
    } catch (err) {
      const message = err instanceof ApiError ? err.body.message : undefined;
      setError(typeof message === 'string' ? message : errorMessage(err));
      setBusy(false);
    }
  }

  async function dismiss() {
    setBusy(true);
    try {
      await del(`/travel/drafts/${draft.id}`);
      toast('Dismissed');
      reload();
      close();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={`Check: ${bookingsTitle(items)}`}
      onClose={close}
      footer={
        <>
          <button
            type="button"
            className="btn danger left"
            disabled={busy}
            onClick={() => void dismiss()}
          >
            Dismiss
          </button>
          <button type="button" className="btn" onClick={close}>
            Cancel
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || kept.length === 0}
            onClick={() => void confirm()}
          >
            {kept.length === items.length
              ? 'Confirm all and save'
              : `Confirm ${kept.length} and save`}
          </button>
        </>
      }
    >
      <form
        id="travel-items"
        className="travel-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void confirm();
        }}
      >
        <div className="hs-state wait">
          <b>Read by Claude</b>
          <span>
            {items.length} bookings found. Check each one and who travels, untick what you do not
            want, then confirm them together.
          </span>
        </div>
        {items.map((item) => (
          <section key={item.id} className="formsec" aria-label={bookingLabel(item)}>
            <Checkbox
              checked={item.keep}
              onChange={(e) => change(item.id, (i) => ({ ...i, keep: e.target.checked }))}
            >
              <b>{bookingLabel(item)}</b>
            </Checkbox>
            {item.keep && (
              <BookingFields
                type={item.type}
                values={{ ...item.fields, reference: item.reference, link: item.link }}
                set={(name, value) => setField(item.id, name, value)}
              />
            )}
          </section>
        ))}
        <ManyOfChips
          label="Who travels on these bookings"
          options={page.team.map((m) => ({ value: m.id, label: m.name }))}
          value={who}
          onChange={(v) => {
            setWho(v);
            setError(null);
          }}
        />
        {reading.passengers.length > 0 && (
          <small className="note">
            Names found in the confirmation: {reading.passengers.join(', ')}
          </small>
        )}
        {draft.file_name && (
          <p className="note travel-intro">
            The confirmation is kept with each of them: {draft.file_name}
          </p>
        )}
        {page.team.length === 0 && (
          <Notice kind="warn">
            Nobody is on the team of this show yet: an admin adds the team.
          </Notice>
        )}
        <p className="err" role="alert">
          {error}
        </p>
      </form>
    </Dialog>
  );
}
